// FILE: components/explore/sections/ReviewsSection.tsx
"use client";

import { memo } from "react";
import HardNavLink from "@/components/shared/HardNavLink";
import { Star, ThumbsUp, MessageSquare, Share2, Gift } from "lucide-react";
import type { ReviewItem } from "../_shared/types";

interface Props {
  items: ReviewItem[];
  loading?: boolean;
}

function ReviewCard({ item }: { item: ReviewItem }) {
  const href = `/reviews/${item.slug || item.id}`;

  return (
    <HardNavLink href={href}
      className="bg-white border border-border rounded-2xl shadow-card p-4 cursor-pointer hover:shadow-[0_8px_30px_rgba(0,0,0,.08)] hover:-translate-y-0.5 transition-all group flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-2.5">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[11px] font-extrabold shrink-0 shadow-sm"
          style={{ background: item.avatarColor }}
        >
          {item.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-bold text-teal-800 truncate">{item.author}</span>
            <span className="text-[10px] text-muted-foreground">{item.time}</span>
          </div>
          {item.org && item.org !== "—" && (
            <span className="inline-flex items-center px-1.5 py-[1px] rounded text-[8px] font-bold bg-teal-50 text-teal-700 border border-teal-200 mt-0.5">
              {item.org}
            </span>
          )}
        </div>
        <button onClick={(e) => e.preventDefault()}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold hover:bg-orange-600 transition-colors shadow-sm shrink-0">
          <Gift className="w-3 h-3" />Donate
        </button>
      </div>

      {/* Stars */}
      <div className="flex items-center gap-0.5 mb-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i}
            className={`w-3.5 h-3.5 ${i <= item.stars ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20"}`}
          />
        ))}
      </div>

      {/* Content */}
      {item.title && (
        <h4 className="text-[13px] font-bold text-foreground mb-1 leading-snug group-hover:text-orange-500 transition-colors">
          {item.title}
        </h4>
      )}
      <p className="text-[12px] text-foreground/70 leading-relaxed line-clamp-3 flex-1 mb-3">
        {item.text}
      </p>

      {/* Actions */}
      <div className="flex items-center gap-1 pt-2.5 border-t border-border">
        <button onClick={(e) => e.preventDefault()}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <ThumbsUp className="w-3.5 h-3.5" />{item.likes} Thích
        </button>
        <div className="w-px h-5 bg-border" />
        <button onClick={(e) => e.preventDefault()}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <MessageSquare className="w-3.5 h-3.5" />{item.comments} Phản hồi
        </button>
        <div className="w-px h-5 bg-border" />
        <button onClick={(e) => e.preventDefault()}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <Share2 className="w-3.5 h-3.5" />Chia sẻ
        </button>
      </div>
    </HardNavLink>
  );
}

function ReviewSkeleton() {
  return (
    <div className="bg-white border border-border rounded-2xl shadow-card p-4 flex flex-col gap-2.5">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-100 shrink-0 animate-pulse" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3.5 w-24 bg-slate-100 rounded animate-pulse" />
          <div className="h-3 w-16 bg-slate-100 rounded animate-pulse" />
        </div>
      </div>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="w-3.5 h-3.5 rounded bg-slate-100 animate-pulse" />
        ))}
      </div>
      <div className="space-y-1.5">
        <div className="h-3.5 w-full bg-slate-100 rounded animate-pulse" />
        <div className="h-3.5 w-3/4 bg-slate-100 rounded animate-pulse" />
        <div className="h-3.5 w-5/6 bg-slate-100 rounded animate-pulse" />
      </div>
      <div className="flex gap-1 pt-2 border-t border-slate-100">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex-1 h-7 bg-slate-100 rounded-lg animate-pulse" />
        ))}
      </div>
    </div>
  );
}

function ReviewsSectionImpl({ items, loading }: Props) {
  if (loading && items.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => <ReviewSkeleton key={i} />)}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-[13px] text-muted-foreground">
        Chưa có review nào
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => (
        <ReviewCard key={item.id} item={item} />
      ))}
    </div>
  );
}

export const ReviewsSection = memo(ReviewsSectionImpl);